import { apiClient } from "@/api/api-client.ts";
import createBaseService from "@/api/base-service.ts";
import type { InvoiceJournalItems, InvoiceJournalLine } from "../../types/invoice";
import type { InvoiceRequest } from "../../types/invoice-request";

const invoiceRequestService = {
	...createBaseService<InvoiceRequest>("/invoice-requests"),

	async updateStatus(id: string, data: { status: string; payment_due_date?: string }): Promise<InvoiceRequest> {
		const response = await apiClient.patch<{ data: InvoiceRequest }>(`/invoice-requests/${id}/status`, data);
		return response.data.data;
	},

	async markSent(
		id: string,
		data: {
			sent_date?: string;
			delivery_method: string;
			recipient_notes?: string;
		},
	): Promise<InvoiceRequest> {
		const response = await apiClient.patch<{ data: InvoiceRequest }>(`/invoice-requests/${id}/mark-sent`, data);
		return response.data.data;
	},

	async updatePaymentStatus(
		id: string,
		data: {
			payment_status: string;
			follow_up_notes?: string;
			payment_amount?: number;
			payment_date?: string;
			payment_reference_number?: string;
			payment_method?: string;
			payment_attachment?: File;
		},
	): Promise<InvoiceRequest> {
		const formData = new FormData();
		formData.append("_method", "PATCH");
		formData.append("payment_status", data.payment_status);
		if (data.follow_up_notes) formData.append("follow_up_notes", data.follow_up_notes);
		if (data.payment_amount !== undefined) formData.append("payment_amount", String(data.payment_amount));
		if (data.payment_date) formData.append("payment_date", data.payment_date);
		if (data.payment_reference_number) formData.append("payment_reference_number", data.payment_reference_number);
		if (data.payment_method) formData.append("payment_method", data.payment_method);
		if (data.payment_attachment) formData.append("payment_attachment", data.payment_attachment);

		const response = await apiClient.post<{ data: InvoiceRequest }>(`/invoice-requests/${id}/payment-status`, formData, {
			headers: { "Content-Type": "multipart/form-data" },
		});
		return response.data.data;
	},

	async uploadAttachment(id: string, file: File): Promise<InvoiceRequest> {
		const formData = new FormData();
		formData.append("payment_attachment", file);
		const response = await apiClient.post<{ data: InvoiceRequest }>(`/invoice-requests/${id}/upload-attachment`, formData, {
			headers: { "Content-Type": "multipart/form-data" },
		});
		return response.data.data;
	},

	async updateCorrections(
		id: string,
		data: {
			items: Array<{
				quantity: number;
				unit_of_measurement: string;
				item_description: string;
				unit_price: number;
				total_price: number;
			}>;
		},
	): Promise<InvoiceRequest> {
		const response = await apiClient.put<{ data: InvoiceRequest }>(`/invoice-requests/${id}/corrections`, data);
		return response.data.data;
	},

	async updatePaymentBreakdown(
		id: string,
		data: {
			deduction_withholding_tax?: number | null;
			deduction_liquidated_damages?: number | null;
			deduction_retention?: number | null;
			deduction_municipal_charge?: number | null;
			deduction_bank_charge?: number | null;
		},
	): Promise<InvoiceRequest> {
		const response = await apiClient.patch<{ data: InvoiceRequest }>(`/invoice-requests/${id}/payment-breakdown`, data);
		return response.data.data;
	},

	async notifyClient(id: string, data: { email: string; subject: string; message: string }): Promise<void> {
		await apiClient.post(`/invoice-requests/${id}/notify-client`, data);
	},

	async pendingCount(): Promise<number> {
		const response = await apiClient.get<{ count: number }>("/invoice-requests/pending-count");
		return response.data.count;
	},

	async getJournalItems(id: string): Promise<InvoiceJournalItems> {
		const response = await apiClient.get<{ data: InvoiceJournalItems }>(`/invoice-requests/${id}/journal-items`);
		return response.data.data;
	},

	async updateJournalLines(id: string, data: { lines: InvoiceJournalLine[] }): Promise<InvoiceJournalItems> {
		const response = await apiClient.put<{ data: InvoiceJournalItems }>(`/invoice-requests/${id}/journal-lines`, data);
		return response.data.data;
	},

	async resetJournalLines(id: string): Promise<InvoiceJournalItems> {
		const response = await apiClient.delete<{ data: InvoiceJournalItems }>(`/invoice-requests/${id}/journal-lines`);
		return response.data.data;
	},

	async pushDrafts(
		id: string,
		status: "draft" | "posted" = "draft",
	): Promise<{
		ar: { attempted: boolean; success: boolean; message: string | null; data: InvoiceJournalItems | null };
		collection: { attempted: boolean; success: boolean; message: string | null; data: InvoiceJournalItems | null };
	}> {
		const response = await apiClient.post<{
			data: {
				ar: { attempted: boolean; success: boolean; message: string | null; data: InvoiceJournalItems | null };
				collection: { attempted: boolean; success: boolean; message: string | null; data: InvoiceJournalItems | null };
			};
		}>(`/invoice-requests/${id}/push-drafts`, { status });
		return response.data.data;
	},
};

export default invoiceRequestService;
